import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { action, computed, makeObservable } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { UserEntity, UserEntityApi, userSchema } from 'entities/User';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { isCancelledRequest } from 'utils/isCancelledRequest';
import { logZodError } from 'utils/logZodError';

import { NotificationsStore } from '../NotificationsStore';

type UserStoreOptions = {
  notificationsStore: NotificationsStore;
};

export class UserStore {
  private readonly _notificationsStore: UserStoreOptions['notificationsStore'];

  readonly loadingStage = new LoadingStageModel();
  private readonly _userCurrentRequest = apiStore.createRequest<UserEntityApi>({
    url: ENDPOINTS.user.current,
  });

  readonly loadingStageLogout = new LoadingStageModel();
  private readonly _logoutRequest = apiStore.createRequest({
    url: ENDPOINTS.auth.logout,
    method: 'POST',
  });

  private readonly _user = new ValueModel<UserEntity | null>(null);
  private _isSyncingNotAccredited = false;

  constructor({ notificationsStore }: UserStoreOptions) {
    this._notificationsStore = notificationsStore;

    makeObservable(this, {
      user: computed,
      hasAccreditation: computed,
      fetchUser: action.bound,
      setUser: action.bound,
      clearUser: action.bound,
      handleNotAccreditedError: action.bound,
      logout: action.bound,
    });
  }

  get user(): UserEntity | null {
    return this._user.value;
  }

  get hasAccreditation(): boolean {
    return this.user?.company?.accreditationStage === 'approved';
  }

  async fetchUser(): Promise<boolean> {
    this.loadingStage.loading();

    const response = await this._userCurrentRequest.call();

    if (response.isError) {
      if (isCancelledRequest(response)) {
        this.loadingStage.reset();

        return false;
      }

      this.loadingStage.error();

      logApiErrWithSentry({
        endpoint: ENDPOINTS.user.current,
        method: 'GET',
        requestPayload: {},
        response,
      });

      return false;
    }

    const parseResponse = userSchema.safeParse(response.data);

    if (parseResponse.error) {
      logZodError(parseResponse.error);

      this.loadingStage.error();

      return false;
    }

    this.setUser(parseResponse.data);

    this.loadingStage.success();

    return true;
  }

  setUser(user: UserEntity): void {
    this._user.change(user);
  }

  clearUser(): void {
    this._user.change(null);
  }

  /**
   * Бэкенд вернул ExcNotAccredited — аккредитация снята в рантайме.
   * Сразу гасим approved в сторе (чтобы показалась заглушка), затем синкаемся через /user/current.
   */
  handleNotAccreditedError(): void {
    if (!this.hasAccreditation || this._isSyncingNotAccredited) {
      return;
    }

    const user = this.user;

    if (!user?.company) {
      return;
    }

    this._isSyncingNotAccredited = true;

    this.setUser({
      ...user,
      company: {
        ...user.company,
        accreditationStage: null,
      },
    });

    void this.fetchUser().finally(() => {
      this._isSyncingNotAccredited = false;
    });
  }

  async logout(): Promise<void> {
    this.loadingStageLogout.loading();

    const response = await this._logoutRequest.call();

    if (response.isError) {
      this._notificationsStore.showErrorByResponse(response);
      this.loadingStageLogout.error();

      logApiErrWithSentry({
        endpoint: ENDPOINTS.auth.logout,
        method: 'POST',
        requestPayload: {},
        response,
      });

      return;
    }

    this.clearUser();
    this.loadingStageLogout.success();
  }
}
